import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift, Wallet, Copy, Users, Send, Award, Trophy } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { AuthImage } from "./AuthImage";
import { useAuth } from "../context/AuthContext";

const WD_LABEL = { PENDING: "Diproses", COMPLETED: "Selesai", REJECTED: "Ditolak" };
const BADGE_STYLE = {
  none: { ring: "border-stone-200", bg: "bg-stone-50", text: "text-stone-400", icon: "text-stone-300" },
  bronze: { ring: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600" },
  silver: { ring: "border-slate-300", bg: "bg-slate-50", text: "text-slate-600", icon: "text-slate-500" },
  gold: { ring: "border-yellow-400", bg: "bg-yellow-50", text: "text-yellow-700", icon: "text-yellow-500" },
  platinum: { ring: "border-emerald-400", bg: "bg-gradient-to-br from-emerald-50 to-sky-50", text: "text-emerald-700", icon: "text-emerald-600" },
};

export function ReferralPanel({ title = "Program Ajak Teman Kedai Mitra" }) {
  const { refresh } = useAuth();
  const [ref, setRef] = useState(null);
  const [board, setBoard] = useState(null);
  const [wd, setWd] = useState({ amount: "", bank_name: "", bank_account: "", bank_owner: "" });
  const [wdSaving, setWdSaving] = useState(false);

  const loadRef = () => {
    api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/referral/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(loadRef, []);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/register?ref=${ref.referral_code}`);
    toast.success("Link referral disalin!");
  };

  const submitWithdraw = async () => {
    const amt = parseInt(wd.amount) || 0;
    if (amt < (ref?.min_withdrawal || 0)) { toast.error(`Minimum penarikan ${rupiah(ref?.min_withdrawal || 0)}`); return; }
    if (amt > (ref?.balance || 0)) { toast.error("Saldo tidak cukup"); return; }
    if (!wd.bank_name || !wd.bank_account || !wd.bank_owner) { toast.error("Lengkapi rekening tujuan"); return; }
    setWdSaving(true);
    try {
      await api.post("/withdrawals", { amount: amt, bank_name: wd.bank_name, bank_account: wd.bank_account, bank_owner: wd.bank_owner });
      toast.success("Permintaan penarikan terkirim. Diproses maks. 2×24 jam (kecuali Minggu).");
      setWd({ amount: "", bank_name: "", bank_account: "", bank_owner: "" });
      if (refresh) await refresh();
      loadRef();
    } catch (e) { toast.error(apiError(e)); } finally { setWdSaving(false); }
  };

  if (!ref || !ref.referral_code) return null;

  return (
    <div className="space-y-5" data-testid="mitra-referral-dashboard">
      <div className="bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 rounded-2xl p-6">
        <p className="font-display font-bold text-lg text-stone-900 flex items-center gap-2"><Gift size={20} className="text-amber-500" />{title}</p>
        <p className="text-sm text-stone-600 mt-1">Tiap ajak orang jadi member pakai kodemu: kamu dapat <b>{rupiah(ref.fee)}</b> ke saldo, mereka dapat diskon <b>{rupiah(ref.discount)}</b>. Contoh: paket {rupiah(50000)} → mereka cukup bayar <b>{rupiah(Math.max(0, 50000 - ref.discount))}</b>, kamu dapat {rupiah(ref.fee)}.</p>
        <p className="text-sm text-emerald-800 mt-1 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">💰 Bonus penjualan: setiap orang yang kamu ajak <b>belanja</b>, kamu dapat <b data-testid="mitra-comm-pct">{ref.commission_pct}%</b> dari ongkir tiap pesanannya. Persen naik makin banyak yang diajak!</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-white rounded-xl border border-stone-200 p-3">
            <p className="text-xs text-stone-500">Kode referralmu</p>
            <p className="font-mono font-bold text-xl text-stone-900 tracking-wider" data-testid="mitra-referral-code">{ref.referral_code}</p>
          </div>
          <button data-testid="mitra-referral-copy" onClick={copyLink} className="bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold px-4 rounded-xl flex items-center justify-center gap-2 py-3"><Copy size={16} />Salin Link Ajak</button>
        </div>
      </div>

      {ref.badge && (() => {
        const bs = BADGE_STYLE[ref.badge.tier] || BADGE_STYLE.none;
        return (
          <div data-testid="mitra-referral-badge" className={`rounded-2xl border-2 ${bs.ring} ${bs.bg} p-5 flex items-center gap-4`}>
            <div className={`w-16 h-16 rounded-full bg-white border-2 ${bs.ring} flex items-center justify-center shrink-0`}><Award className={bs.icon} size={32} /></div>
            <div className="flex-1">
              <p className="text-xs text-stone-500">Badge Duta Kedai Impor</p>
              <p className={`font-display font-extrabold text-xl ${bs.text}`} data-testid="mitra-badge-name">{ref.badge.name}</p>
              {ref.badge.next_name ? (
                <p className="text-xs text-stone-500 mt-1">Ajak <b>{ref.badge.next_at - ref.badge.count}</b> orang lagi untuk naik ke <b>{ref.badge.next_name}</b></p>
              ) : (
                <p className="text-xs text-emerald-700 mt-1 font-semibold">Level tertinggi tercapai! 🎉</p>
              )}
            </div>
          </div>
        );
      })()}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs text-stone-500 flex items-center gap-1.5"><Wallet size={14} />Saldo Referral</p>
          <p className="text-3xl font-display font-extrabold text-emerald-700 mt-1" data-testid="mitra-referral-balance">{rupiah(ref.balance)}</p>
          <p className="text-xs text-stone-400 mt-1">Minimum penarikan {rupiah(ref.min_withdrawal)}.</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs text-stone-500 flex items-center gap-1.5"><Users size={14} />Berhasil Diajak</p>
          <p className="text-3xl font-display font-extrabold text-stone-900 mt-1" data-testid="mitra-referral-count">{ref.history.length}</p>
          <p className="text-xs text-stone-400 mt-1">Dihitung saat membership mereka disetujui.</p>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="mitra-commission-summary">
        <p className="text-xs text-stone-500 flex items-center gap-1.5"><Wallet size={14} />Total Komisi Penjualan Diterima</p>
        <p className="text-3xl font-display font-extrabold text-amber-600 mt-1" data-testid="mitra-commission-total">{rupiah(ref.commission_total || 0)}</p>
        <p className="text-xs text-stone-400 mt-1">Komisi <b>{ref.commission_pct}%</b> dari ongkir tiap pesanan orang yang diajak. Mereka harus transaksi minimal 1× per {ref.commission_active_days || 30} hari agar komisi tetap aktif.</p>
      </div>

      {board && board.leaderboard.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="mitra-referral-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-stone-800 flex items-center gap-2"><Trophy size={18} className="text-amber-500" />Top Duta Bulan Ini</p>
            {board.my_rank && <span className="text-xs text-stone-500">Peringkatmu: <b className="text-emerald-700">#{board.my_rank}</b></span>}
          </div>
          <div className="space-y-1.5">
            {board.leaderboard.map((b) => (
              <div key={b.rank} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${b.is_me ? "bg-emerald-50 border border-emerald-200" : "bg-stone-50"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${b.rank === 1 ? "bg-amber-100 text-amber-700" : b.rank === 2 ? "bg-slate-100 text-slate-600" : b.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-stone-100 text-stone-500"}`}>{b.rank}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{b.name}{b.is_me && <span className="text-emerald-600 text-xs font-semibold"> (kamu)</span>}</p>
                  <p className="text-[11px] text-stone-400">{b.badge}</p>
                </div>
                <span className="text-sm font-semibold text-stone-700 shrink-0">{b.count} <span className="text-xs text-stone-400 font-normal">orang</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="mitra-withdraw-form">
        <p className="font-semibold text-stone-800 flex items-center gap-2"><Send size={16} className="text-emerald-600" />Tarik Saldo</p>
        <p className="text-xs text-stone-500">Penarikan diproses maksimal <b>2×24 jam</b> (tidak termasuk hari Minggu).</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="mitra-wd-amount" type="number" value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })} placeholder={`Jumlah (min ${rupiah(ref.min_withdrawal)})`} className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
          <input data-testid="mitra-wd-bank" value={wd.bank_name} onChange={(e) => setWd({ ...wd, bank_name: e.target.value })} placeholder="Nama Bank / e-wallet" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
          <input data-testid="mitra-wd-account" value={wd.bank_account} onChange={(e) => setWd({ ...wd, bank_account: e.target.value })} placeholder="No. Rekening / HP" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
          <input data-testid="mitra-wd-owner" value={wd.bank_owner} onChange={(e) => setWd({ ...wd, bank_owner: e.target.value })} placeholder="Atas Nama" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
        </div>
        <button data-testid="mitra-wd-submit" onClick={submitWithdraw} disabled={wdSaving || ref.balance < ref.min_withdrawal} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
          {wdSaving && <Loader2 className="animate-spin" size={16} />}Ajukan Penarikan
        </button>
        {ref.balance < ref.min_withdrawal && <p className="text-xs text-stone-400 text-center">Saldo belum mencapai minimum penarikan.</p>}
      </div>

      {ref.withdrawals.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="mitra-withdraw-history">
          <p className="font-semibold text-stone-800 mb-3">Riwayat Penarikan</p>
          <div className="space-y-2">
            {ref.withdrawals.map((w) => (
              <div key={w.id} className="border border-stone-100 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-stone-800">{rupiah(w.amount)}</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${w.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : w.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{WD_LABEL[w.status]}</span>
                </div>
                <p className="text-xs text-stone-400 mt-0.5">{w.bank_name} · {w.bank_account} · {w.bank_owner}</p>
                {w.status === "REJECTED" && w.reject_reason && <p className="text-xs text-red-600 mt-1">Alasan: {w.reject_reason} (saldo dikembalikan)</p>}
                {w.status === "COMPLETED" && w.proof_photo && (
                  <div className="mt-2">
                    <p className="text-[11px] text-stone-500 mb-1">Bukti transfer dari admin:</p>
                    <AuthImage path={w.proof_photo} className="w-32 rounded-lg border border-stone-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {ref.commission_history && ref.commission_history.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="mitra-commission-history">
          <p className="font-semibold text-stone-800 mb-3">Riwayat Komisi Penjualan</p>
          <div className="space-y-2">
            {ref.commission_history.map((c, i) => (
              <div key={i} className="flex items-center justify-between border-b border-stone-50 last:border-0 py-2 text-sm">
                <div>
                  <p className="font-medium text-stone-800">{c.referee_name} <span className="text-stone-400 text-xs">· {c.referee_email}</span></p>
                  <p className="text-xs text-stone-400">{c.pct}% × ongkir {rupiah(c.shipping_fee)} · {new Date(c.created_at).toLocaleDateString("id-ID")}</p>
                </div>
                <span className="text-amber-600 font-semibold text-sm">+{rupiah(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {ref.history.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="mitra-referral-history">
          <p className="font-semibold text-stone-800 mb-3">Riwayat Ajak Teman</p>
          <div className="space-y-2">
            {ref.history.map((h, i) => (
              <div key={i} className="flex items-center justify-between border-b border-stone-50 last:border-0 py-2 text-sm">
                <div>
                  <p className="font-medium text-stone-800">{h.referee_name} <span className="text-stone-400 text-xs">· {h.referee_email}</span></p>
                  <p className="text-xs text-stone-400">{h.plan} · {new Date(h.created_at).toLocaleDateString("id-ID")}</p>
                </div>
                <span className="text-emerald-700 font-semibold text-sm">+{rupiah(h.fee)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
